/**
 * FORGE React Context Provider
 *
 * Global state management for FORGE UI components
 * Provides mode, requirement, execution state, page context, and SSE connection
 */

import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { useForgeSSE } from './hooks/useForgeSSE';
import { forgeApi } from './services/forgeApi';
import type {
  ForgeMode,
  ForgeRequirement,
  PageContext,
  ForgeContextValue,
  ForgeProviderProps,
  DetailedModeRequest,
  DetailedDraft,
  ExecutionStatus,
} from './types/forge';

export const ForgeContext = createContext<ForgeContextValue | undefined>(undefined);

/**
 * FORGE Context Provider
 * Wraps the application to provide global FORGE state and functionality
 */
export const ForgeProvider: React.FC<ForgeProviderProps> = ({
  children,
  serviceId,
  forgeApiUrl,
}) => {
  // UI State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [mode, setMode] = useState<ForgeMode>('quick');

  // Requirement State
  const [requirement, setRequirement] = useState<ForgeRequirement | null>(null);
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Detailed Mode Draft State (persists across panel open/close)
  const [detailedDraft, setDetailedDraft] = useState<DetailedDraft | null>(null);

  // Page Context
  const [pageContext, setPageContext] = useState<PageContext | null>(null);

  // SSE Connection
  const sse = useForgeSSE();

  /**
   * Set FORGE API URL if provided
   */
  if (forgeApiUrl && typeof window !== 'undefined') {
    (window as any).__FORGE_API_URL__ = forgeApiUrl;
  }

  /**
   * Capture current page context
   */
  const capturePageContext = useCallback((): PageContext => {
    if (pageContext) {
      return pageContext;
    }

    if (typeof window !== 'undefined') {
      return {
        url: window.location.href,
        title: document.title,
        timestamp: new Date().toISOString(),
        serviceId,
      };
    }

    return {
      url: '',
      title: '',
      timestamp: new Date().toISOString(),
      serviceId,
    };
  }, [pageContext, serviceId]);

  /**
   * Submit Quick Mode request
   */
  const submitQuick = useCallback(
    async (message: string): Promise<void> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const context = capturePageContext();
        const response = await forgeApi.quickMode.submit({
          message,
          pageContext: context,
        });

        setRequirement(response.requirement);

        // Auto-trigger execution for quick tier
        if (response.tier === 'quick') {
          setIsExecuting(true);
          await forgeApi.execution.trigger({ requirementId: response.requirement.id });
          sse.connect(response.requirement.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit request');
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [capturePageContext, sse]
  );

  /**
   * Submit Detailed Mode request
   */
  const submitDetailed = useCallback(
    async (data: DetailedModeRequest): Promise<void> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const context = capturePageContext();
        const requestWithContext = {
          ...data,
          pageContext: context,
        };

        const response = await forgeApi.detailedMode.submit(requestWithContext);
        setRequirement(response.requirement);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit request');
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [capturePageContext]
  );

  /**
   * Trigger execution for a requirement
   */
  const triggerExecution = useCallback(
    async (requirementId: number): Promise<void> => {
      setIsExecuting(true);
      await forgeApi.execution.trigger({ requirementId });
      sse.connect(requirementId);
    },
    [sse]
  );

  /**
   * Disconnect SSE
   */
  const disconnectSSE = useCallback(() => {
    sse.disconnect();
    setIsExecuting(false);
  }, [sse]);

  /**
   * Reset detailed mode draft only
   */
  const resetDraft = useCallback(() => {
    setDetailedDraft(null);
  }, []);

  /**
   * Reset all state (including draft)
   */
  const reset = useCallback(() => {
    setRequirement(null);
    setIsExecuting(false);
    setExecutionStatus(null);
    setDetailedDraft(null);
    sse.disconnect();
  }, [sse]);

  // Update execution status from SSE
  React.useEffect(() => {
    if (sse.executionStatus) {
      setExecutionStatus(sse.executionStatus);
    }
  }, [sse.executionStatus]);

  // Update isExecuting based on SSE connection
  React.useEffect(() => {
    setIsExecuting(sse.isConnected);
  }, [sse.isConnected]);

  const value: ForgeContextValue = {
    // UI State
    isOpen,
    setIsOpen,
    mode,
    setMode,

    // Requirement State
    requirement,
    setRequirement,
    isExecuting,
    setIsExecuting,
    isSubmitting,
    executionStatus,
    setExecutionStatus,
    error,

    // Detailed Mode Draft State
    detailedDraft,
    setDetailedDraft,
    resetDraft,

    // Page Context
    pageContext,
    setPageContext,

    // Actions
    submitQuick,
    submitDetailed,
    triggerExecution,
    disconnect: disconnectSSE,
    reset,
  };

  return <ForgeContext.Provider value={value}>{children}</ForgeContext.Provider>;
};

export default ForgeProvider;
